import { addDays, eachDayOfInterval, format, parseISO, startOfWeek } from 'date-fns';
import type { ProjectOption } from '../../components/SearchableProjectDropdown/config';
import { formatTime, gapMinutes, LunchBreak, minutesToTime, timeToMinutes } from '../../utils/timesheetTime';
import { LEAVE_TYPE_LABELS, LeaveType, ProjectGroup, TimesheetEntry } from './config';

/**
 * Everything the Timesheets pages show is derived from one list of entries.
 * These helpers are pure so the grid, the day view and the totals cannot
 * drift apart, and so a refetch is the only thing that ever changes them.
 */

/** Monday..Sunday for a week ending on the given Sunday. */
export const weekDays = (weekEndingDate: string): Date[] => {
  if (!weekEndingDate) return [];
  const end = parseISO(weekEndingDate);
  return eachDayOfInterval({ start: startOfWeek(end, { weekStartsOn: 1 }), end });
};

export const dateKey = (d: Date): string => format(d, 'yyyy-MM-dd');

/** Monday of the week, as yyyy-MM-dd. */
export const weekStartKey = (weekEndingDate: string): string =>
  dateKey(addDays(parseISO(weekEndingDate), -6));

export const isDateInWeek = (date: string, weekEndingDate: string): boolean =>
  date >= weekStartKey(weekEndingDate) && date <= weekEndingDate;

/**
 * The row identity. Project and stage ids overlap, so the key carries which
 * one it is; the view's project_type is what the entry resolved to.
 */
export const groupKeyFor = (entry: Pick<TimesheetEntry, 'project_id' | 'leave_type' | 'project_type'>): string =>
  entry.leave_type
    ? `leave_${entry.leave_type}`
    : `${entry.project_type === 'stage' ? 'stage' : 'project'}_${entry.project_id}`;

export const groupKeyForOption = (option: ProjectOption): string =>
  `${option.entity_type === 'stage' ? 'stage' : 'project'}_${option.id}`;

const groupFromEntry = (entry: TimesheetEntry): ProjectGroup => {
  if (entry.leave_type) {
    return {
      groupKey: groupKeyFor(entry),
      projectId: null,
      leaveType: entry.leave_type,
      isLeave: true,
      projectName: LEAVE_TYPE_LABELS[entry.leave_type],
      jobNumber: null,
      entityType: 'leave',
      parentProjectId: null,
      stageDescription: null,
      parentDescription: null,
      entries: []
    };
  }
  const isStage = entry.project_type === 'stage';
  return {
    groupKey: groupKeyFor(entry),
    projectId: entry.project_id,
    leaveType: null,
    isLeave: false,
    // The view prefixes a stage name with "[stage] parent"; the grid shows
    // the stage on its own line and the parent underneath.
    projectName: isStage && entry.stage_description ? entry.stage_description : entry.project_name,
    jobNumber: entry.project_job_number === 'N/A' ? null : entry.project_job_number,
    entityType: isStage ? 'stage' : 'project',
    parentProjectId: entry.parent_project_id ?? null,
    stageDescription: entry.stage_description ?? null,
    parentDescription: entry.parent_description ?? null,
    entries: []
  };
};

/**
 * How a project or stage option is written wherever it is shown: the job
 * code ("12345" or "12345.2"), the name (a stage's own description, else the
 * project's), and for a stage the parent project as a sub line.
 */
export const describeOption = (option: ProjectOption): { code: string; name: string; sub: string | null } => {
  const isStage = option.entity_type === 'stage';
  return {
    code: isStage && option.stage_id ? `${option.job_number}.${option.stage_id}` : String(option.job_number),
    name: isStage ? option.stage_description || option.display_name : option.display_name,
    sub: isStage ? option.parent_description ?? null : null
  };
};

/** A blank grid row for a project or stage picked from the Recent strip. */
export const groupFromOption = (option: ProjectOption): ProjectGroup => {
  const isStage = option.entity_type === 'stage';
  const projectId = Number(option.id);
  const { code, name } = describeOption(option);
  return {
    groupKey: groupKeyForOption(option),
    projectId,
    leaveType: null,
    isLeave: false,
    projectName: name,
    jobNumber: code,
    entityType: isStage ? 'stage' : 'project',
    parentProjectId: option.parent_project_id ?? null,
    stageDescription: option.stage_description ?? null,
    parentDescription: option.parent_description ?? null,
    entries: []
  };
};

/** A blank grid row for a leave type. */
export const groupFromLeave = (leaveType: LeaveType): ProjectGroup => ({
  groupKey: `leave_${leaveType}`,
  projectId: null,
  leaveType,
  isLeave: true,
  projectName: LEAVE_TYPE_LABELS[leaveType],
  jobNumber: null,
  entityType: 'leave',
  parentProjectId: null,
  stageDescription: null,
  parentDescription: null,
  entries: []
});

const compareGroups = (a: ProjectGroup, b: ProjectGroup): number => {
  if (a.isLeave !== b.isLeave) return a.isLeave ? 1 : -1;
  if (a.jobNumber && b.jobNumber && a.jobNumber !== b.jobNumber) {
    // Numeric job order, then stage id.
    const [aj, as = ''] = a.jobNumber.split('.');
    const [bj, bs = ''] = b.jobNumber.split('.');
    if (aj !== bj) return Number(aj) - Number(bj);
    return as.localeCompare(bs, undefined, { numeric: true });
  }
  return a.projectName.localeCompare(b.projectName);
};

/**
 * Group the week's entries by project/stage/leave, then merge in any blank
 * rows. A blank row whose group now has entries is dropped (the real row
 * wins). Projects first in job-number order, then leave.
 */
export const groupEntries = (entries: TimesheetEntry[], blankRows: ProjectGroup[] = []): ProjectGroup[] => {
  const groups = new Map<string, ProjectGroup>();
  for (const entry of entries) {
    const key = groupKeyFor(entry);
    let group = groups.get(key);
    if (!group) {
      group = groupFromEntry(entry);
      groups.set(key, group);
    }
    group.entries.push(entry);
  }
  for (const blank of blankRows) {
    if (!groups.has(blank.groupKey)) {
      groups.set(blank.groupKey, { ...blank, entries: [] });
    }
  }
  return Array.from(groups.values()).sort(compareGroups);
};

/** Entries of one group on one date, in creation order. */
export const entriesFor = (group: ProjectGroup, date: string): TimesheetEntry[] =>
  group.entries.filter((e) => e.date === date).sort((a, b) => a.created_at.localeCompare(b.created_at));

/** Hours per yyyy-MM-dd. */
export const dayTotals = (entries: TimesheetEntry[]): Map<string, number> => {
  const totals = new Map<string, number>();
  for (const e of entries) totals.set(e.date, (totals.get(e.date) ?? 0) + Number(e.hours));
  return totals;
};

export const sumHours = (entries: TimesheetEntry[]): number =>
  entries.reduce((sum, e) => sum + Number(e.hours), 0);

export interface WeekStats {
  total: number;
  project: number;
  leave: number;
  daysLogged: number;
}

export const weekStats = (entries: TimesheetEntry[]): WeekStats => {
  const leave = sumHours(entries.filter((e) => e.leave_type));
  const total = sumHours(entries);
  return {
    total,
    project: total - leave,
    leave,
    daysLogged: new Set(entries.filter((e) => Number(e.hours) > 0).map((e) => e.date)).size
  };
};

/**
 * A cell is "simple" when the grid can edit it inline: at most one entry,
 * with no description and no clock times. Anything richer opens the day
 * view or the editor so nothing is lost by typing a number over it.
 */
export const isSimpleCell = (cellEntries: TimesheetEntry[]): boolean =>
  cellEntries.length === 0 ||
  (cellEntries.length === 1 && !cellEntries[0].description && !cellEntries[0].start_time);

/** Timed entries by start time, then untimed by creation. */
export const sortDayEntries = (entries: TimesheetEntry[]): TimesheetEntry[] =>
  [...entries].sort((a, b) => {
    if (a.start_time && b.start_time) return formatTime(a.start_time).localeCompare(formatTime(b.start_time));
    if (a.start_time) return -1;
    if (b.start_time) return 1;
    return a.created_at.localeCompare(b.created_at);
  });

/**
 * A day's lunch break and whether the day has its own (rather than the
 * employee's usual one). Every entry row carries its day's lunch from the
 * view, so any entry on the day will do; a day with no entries yet has the
 * usual break.
 */
export const dayLunch = (
  entries: TimesheetEntry[],
  date: string,
  usual: LunchBreak
): { lunch: LunchBreak; isCustom: boolean } => {
  const row = entries.find((e) => e.date === date && e.lunch_start_time);
  if (!row) return { lunch: usual, isCustom: false };
  return {
    lunch: { start: formatTime(row.lunch_start_time), minutes: Number(row.lunch_minutes ?? 0) },
    isCustom: Boolean(row.lunch_is_custom)
  };
};

export type DayRow =
  | { kind: 'entry'; entry: TimesheetEntry; overlaps: boolean }
  | { kind: 'gap'; minutes: number; key: string }
  | { kind: 'lunch'; key: string };

/**
 * The day view's rows: entries in order with a gap row wherever nothing is
 * booked, and an overlap flag when an entry starts before the previous one
 * ends. Overlaps are shown, never blocked.
 *
 * Once the day's timed entries reach lunch (the latest one ends at or after
 * its start), the lunch break is a row of its own, placed by its start:
 * between two entries when it was taken in a gap, or straight after the
 * entry it falls inside. Before that it is not shown at all: a day filled in
 * as it goes would otherwise show a gap "until lunch" and a lunch row above
 * an add row that starts earlier than it. It counts as booked time for the
 * gaps (a gap is time nobody accounted for) but never as an overlap: an
 * entry that spans lunch simply loses that part of its hours. A day with no
 * lunch (0 minutes) still gets the row, at the usual start, so one can be
 * added back.
 */
export const dayRows = (entries: TimesheetEntry[], lunch: LunchBreak): DayRow[] => {
  const sorted = sortDayEntries(entries);
  const rows: DayRow[] = [];
  const lunchStart = formatTime(lunch.start);
  const lunchEnd = lunch.minutes > 0 ? minutesToTime(timeToMinutes(lunchStart) + lunch.minutes) : null;
  const latestEnd = lastEndTime(sorted);
  let lunchPlaced = !latestEnd || gapMinutes(lunchStart, latestEnd) < 0;
  // End of the latest entry so far (for overlaps) and of anything booked,
  // entries or lunch (for gaps).
  let prevEnd: string | null = null;
  let coveredEnd: string | null = null;

  const pushGapBefore = (start: string, key: string) => {
    if (!coveredEnd) return;
    const gap = gapMinutes(coveredEnd, start);
    if (gap > 0) rows.push({ kind: 'gap', minutes: gap, key });
  };
  const cover = (end: string) => {
    if (!coveredEnd || gapMinutes(coveredEnd, end) > 0) coveredEnd = formatTime(end);
  };
  const placeLunch = () => {
    lunchPlaced = true;
    if (lunchEnd) {
      pushGapBefore(lunchStart, 'gap-lunch');
      cover(lunchEnd);
    }
    rows.push({ kind: 'lunch', key: 'lunch' });
  };

  for (const entry of sorted) {
    const timed = Boolean(entry.start_time && entry.end_time);
    // Lunch goes before the first entry that starts at or after it, and
    // before the untimed entries if no timed entry does.
    if (!lunchPlaced && (!timed || gapMinutes(lunchStart, entry.start_time as string) >= 0)) placeLunch();
    let overlaps = false;
    if (timed) {
      const start = entry.start_time as string;
      const end = entry.end_time as string;
      pushGapBefore(start, `gap-${entry.id}`);
      if (prevEnd && gapMinutes(prevEnd, start) < 0) overlaps = true;
      prevEnd = prevEnd && gapMinutes(prevEnd, end) < 0 ? prevEnd : end;
      cover(end);
    }
    rows.push({ kind: 'entry', entry, overlaps });
  }
  if (!lunchPlaced) placeLunch();
  return rows;
};

/**
 * A timed entry that runs into the lunch break from one side: it ends inside
 * the break, or starts inside it and carries on past. That part of it is not
 * booked, and it almost always means the lunch was taken at another time,
 * not that the work stopped: 8:30-12:20 over a 12:00 lunch books 3.5 h, not
 * 3.75. `suggestion` is the same-length lunch moved to butt against the entry
 * (12:20-13:20). An entry that spans the whole break is not a clash; that is
 * what the deduction is for.
 */
export interface LunchClash {
  entry: TimesheetEntry;
  /** Minutes of the entry inside the break. */
  minutes: number;
  /** True when the entry ends inside the break, false when it starts inside it. */
  endsInside: boolean;
  suggestion: LunchBreak;
}

export const lunchClash = (entries: TimesheetEntry[], lunch: LunchBreak): LunchClash | null => {
  if (lunch.minutes <= 0) return null;
  const lunchStart = timeToMinutes(formatTime(lunch.start));
  const lunchEnd = lunchStart + lunch.minutes;
  for (const entry of sortDayEntries(entries)) {
    if (!entry.start_time || !entry.end_time) continue;
    const start = timeToMinutes(formatTime(entry.start_time));
    const end = timeToMinutes(formatTime(entry.end_time));
    if (start < lunchStart && end > lunchStart && end < lunchEnd) {
      return { entry, minutes: end - lunchStart, endsInside: true, suggestion: { start: minutesToTime(end), minutes: lunch.minutes } };
    }
    if (start > lunchStart && start < lunchEnd && end > lunchEnd) {
      return { entry, minutes: lunchEnd - start, endsInside: false, suggestion: { start: minutesToTime(start - lunch.minutes), minutes: lunch.minutes } };
    }
  }
  return null;
};

/** The end of the latest timed entry on the day, "HH:mm", or null. */
export const lastEndTime = (entries: TimesheetEntry[]): string | null => {
  let latest: string | null = null;
  for (const e of entries) {
    if (e.end_time && (!latest || gapMinutes(latest, e.end_time) > 0)) latest = formatTime(e.end_time);
  }
  return latest;
};

/** Job number + name for an entry, for the day view and the list. */
export const entryLabel = (entry: TimesheetEntry): { code: string | null; name: string; sub: string | null } => {
  if (entry.leave_type) return { code: null, name: LEAVE_TYPE_LABELS[entry.leave_type], sub: null };
  const isStage = entry.project_type === 'stage';
  return {
    code: entry.project_job_number === 'N/A' ? null : entry.project_job_number,
    name: isStage && entry.stage_description ? entry.stage_description : entry.project_name,
    sub: isStage ? entry.parent_description : null
  };
};
